import { atom } from "jotai";
import { QueryAtom } from "@/api/types/query.types";
import { Connector } from "@/entities/ConnectorService/Connector.model";
import { StatementType } from "@/entities/DataAssociation/File.model";
import { ConnectorFilesResponse, UploadsResponse } from "../labeling.queries";
import { draftListAtom } from "./draft.state";
import { FileTree } from "../Labeling.types";

export const uploadsAtom = atom<QueryAtom<UploadsResponse>>({
    isLoading: false,
    isError: false,
    data: null,
});

export const connectorsAtom = atom<QueryAtom<Connector[]>>({
    isLoading: false,
    isError: false,
    data: null,
});

export const connectorFilesAtom = atom<QueryAtom<ConnectorFilesResponse[]>>({
    isLoading: false,
    isError: false,
    data: null,
});

type StatementTypeKey = Exclude<StatementType, StatementType.MIXED>;

type ConnectorTreeList = {
    sourceConnectorId: string;
    sourceConnectorType: string;
    hasUnlabeledFiles: boolean;
    mostRecent: Record<StatementTypeKey, FileTree | null>;
    historical: Record<StatementTypeKey, FileTree | null>;
    current: FileTree | null;
};

export const connectorTreeListAtom = atom(
    (get): QueryAtom<ConnectorTreeList[]> => {
        const connectorFiles = get(connectorFilesAtom);
        const draftList = get(draftListAtom);
        console.log("connectorTreeListAtom recomputing:", {
            draftsLength: draftList.data?.length,
            connectorFilesLength: connectorFiles.data?.length,
        });

        const toFileTree = (
            file: NonNullable<
                ConnectorFilesResponse["mostRecent"][StatementTypeKey]
            >,
        ): FileTree => {
            const draft = draftList.data?.find((d) => d.fileId === file.id);
            return {
                name: file.name,
                documentKind: file.documentKind,
                date: file.createdAt,
                id: file.id,
                draft: draft
                    ? {
                          id: draft.id,
                          status:
                              draft.status === "committed"
                                  ? "committed"
                                  : "uncommitted",
                          date: draft.createdAt,
                      }
                    : null,
                labeledFiles: file.labeledFiles.map((lf) => ({
                    id: lf.id,
                    date: lf.name,
                })),
            };
        };

        if (!connectorFiles.data) {
            return {
                isLoading: connectorFiles.isLoading,
                isError: connectorFiles.isError,
                data: null,
            };
        }

        const data = connectorFiles.data.map((connector) => {
            const allFiles = [
                connector.mostRecent.income_statement,
                connector.mostRecent.balance_sheet,
                connector.historical.income_statement,
                connector.historical.balance_sheet,
                connector.current,
            ].filter((f): f is NonNullable<typeof f> => f !== null);

            const hasUnlabeledFiles = allFiles.some(
                (file) => file.labeledFiles.length === 0,
            );

            return {
                sourceConnectorId: connector.sourceConnectorId,
                sourceConnectorType: connector.sourceConnectorType,
                hasUnlabeledFiles,
                mostRecent: {
                    income_statement: connector.mostRecent.income_statement
                        ? toFileTree(connector.mostRecent.income_statement)
                        : null,
                    balance_sheet: connector.mostRecent.balance_sheet
                        ? toFileTree(connector.mostRecent.balance_sheet)
                        : null,
                },
                historical: {
                    income_statement: connector.historical.income_statement
                        ? toFileTree(connector.historical.income_statement)
                        : null,
                    balance_sheet: connector.historical.balance_sheet
                        ? toFileTree(connector.historical.balance_sheet)
                        : null,
                },
                current: connector.current
                    ? toFileTree(connector.current)
                    : null,
            };
        });

        return {
            isLoading: connectorFiles.isLoading,
            isError: connectorFiles.isError,
            data,
        };
    },
);
